import {
  type PublicVendorApi,
  type VendorApiExistingSettings,
  settingTypes,
} from '@ipfabric/utils';
import type { ArubaNewCentralFormValues } from './ArubaNewCentralForm';
import type { AwsFormValues } from './AwsForm';
import type { AzureFormValues } from './AzureForm';
import type { CheckPointFormValues } from './CheckpointApiForm';
import type { CiscoApicFormValues } from './CiscoApicForm';
import type { CiscoFmcFormValues } from './CiscoFmcForm';
import type { ForcePointFormValues } from './ForcePointForm';
import type { GCPFormValues } from './GoogleCloudPlatformForm';
import type { JuniperMistFormValues } from './JuniperMistForm';
import type { MerakiFormValues } from './MerakiForm';
import type { NsxTFormValues } from './NsxTForm';
import type { PrismaFormValues } from './PrismaForm';
import type { RuckusFormValues } from './RuckusForm';
import type { SilverPeakFormValues } from './SilverPeakForm';
import type { VeloCloudFormValues } from './VeloCloudForm';
import type { VersaFormValues } from './VersaForm';
import type { ViptelaFormValues } from './ViptelaForm';

export const isVendorAws = (
  vendor: Vendor,
): vendor is settingTypes.AwsApiSettings =>
  vendor.type === settingTypes.vendorApiNames.aws;

export type VendorApiKey = keyof typeof settingTypes.vendorApiNames;
export type VendorApiName = (typeof settingTypes.vendorApiNames)[VendorApiKey];

export interface TemporaryClientProperties {
  testOnly?: boolean;
  /**
   * The field exists when a user create a new vendor and doesn't save it yet.
   * When client sends a request to the server, the field is removed.
   */
  unsaved?: boolean;
  /**
   * The field is used to display additional information about vendors in the `Details` column in the Vendors API table.
   * The field is used by only the client and is not sent to the server.
   */
  details?: string;
  /**
   * `baseUrl` is kept in old AWS vendor data; should be removed after the ticket NIM-15297 would be completed
   * @deprecated
   */
  baseUrl?: string;
  /**
   * `apiVersion`, `baseUrl` are sent from server, but server does not expect to get them; unused on the client side.
   * Should be removed after the ticket NIM-15297 would be completed.
   * @deprecated
   */
  apiVersion?: string;
}

export type FormVendorValues =
  | ArubaNewCentralFormValues
  | AwsFormValues
  | AzureFormValues
  | CiscoFmcFormValues
  | CiscoApicFormValues
  | JuniperMistFormValues
  | NsxTFormValues
  | RuckusFormValues
  | SilverPeakFormValues
  | VersaFormValues
  | ViptelaFormValues
  | MerakiFormValues
  | CheckPointFormValues
  | ForcePointFormValues
  | PrismaFormValues
  | GCPFormValues
  | VeloCloudFormValues
  | BaseVendorFormValues<settingTypes.ArubaNewCentralApiSettings>
  | BaseVendorFormValues<settingTypes.F5osAApiSettings>
  | BaseVendorFormValues<settingTypes.F5osCApiSettings>;

export type VendorSubmit = TemporaryClientProperties &
  (
    | settingTypes.ArubaNewCentralApiSettings
    | settingTypes.UpdateArubaNewCentralApiSettings
    | settingTypes.AwsApiSettings
    | settingTypes.UpdateAwsApiSettings
    | settingTypes.AzureApiSettings
    | settingTypes.UpdateAzureApiSettings
    | settingTypes.CiscoFmcApiSettings
    | settingTypes.UpdateCiscoFmcApiSettings
    | settingTypes.CiscoApicApiSettings
    | settingTypes.UpdateCiscoApicApiSettings
    | settingTypes.F5osAApiSettings
    | settingTypes.UpdateF5osAApiSettings
    | settingTypes.F5osCApiSettings
    | settingTypes.UpdateF5osCApiSettings
    | settingTypes.JuniperMistApiSettings
    | settingTypes.UpdateJuniperMistApiSettings
    | settingTypes.NsxTApiSettings
    | settingTypes.UpdateNsxTApiSettings
    | settingTypes.RuckusVszApiSettings
    | settingTypes.UpdateRuckusVszApiSettings
    | settingTypes.SilverPeakApiSettings
    | settingTypes.UpdateSilverPeakApiSettings
    | settingTypes.VersaApiSettings
    | settingTypes.UpdateVersaApiSettings
    | settingTypes.ViptelaApiSettings
    | settingTypes.UpdateViptelaApiSettings
    | settingTypes.MerakiApiSettings
    | settingTypes.UpdateMerakiApiSettings
    | settingTypes.CheckPointApiSettings
    | settingTypes.UpdateCheckPointApiSettings
    | settingTypes.ForcePointApiSettings
    | settingTypes.UpdateForcePointApiSettings
    | settingTypes.PrismaApiSettings
    | settingTypes.UpdatePrismaApiSettings
    | settingTypes.GcpApiSettings
    | settingTypes.UpdateGcpApiSettings
    | settingTypes.VeloCloudApiSettings
    | settingTypes.UpdateVeloCloudApiSettings
  );

export type Vendor = TemporaryClientProperties & PublicVendorApi;
export type ExistingVendor = VendorApiExistingSettings & Vendor;

export interface VendorDialogContainerPassthruProps {
  setType: (type: settingTypes.VendorApiType) => void;
  type: settingTypes.VendorApiType;
  editName?: string;
}

export type BaseVendorFormValues<TCreateVendor> = TCreateVendor &
  TemporaryClientProperties &
  Partial<VendorApiExistingSettings>;

export type VendorDialogProps<TFormValues, TCreate, TUpdate> = {
  initialValues: TFormValues;
  vendorDialogContainerPassthruProps: VendorDialogContainerPassthruProps;
} & (
  | {
      dialogType: 'add';
      onSubmit: (values: TCreate) => void;
    }
  | {
      dialogType: 'edit';
      onSubmit: (values: TUpdate) => void;
    }
);
